import { useReducer, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClipboard } from './useClipboard';
import { extractErrorWithDetail } from '../utils/errorUtils';

/**
 * Generic CRUD service interface that entity services should implement
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface EntityCrudService<TEntity, TCreateDto, TUpdateDto, TTestResult = any> {
  getAll: () => Promise<TEntity[]>;
  create: (dto: TCreateDto) => Promise<TEntity>;
  update: (id: string, dto: TUpdateDto) => Promise<TEntity>;
  delete: (id: string) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  testAccess?: (id: string, ...args: any[]) => Promise<TTestResult>;
}

/**
 * Configuration for entity management behavior
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface EntityManagementConfig<TEntity, TFormData, TCreateDto = any, TUpdateDto = any, TTestResult = any> {
  /** Service that provides CRUD operations */
  service: EntityCrudService<TEntity, TCreateDto, TUpdateDto, TTestResult>;
  /** Cache key for the entity list read, scoping the cached list to the caller. */
  queryKey: readonly unknown[];
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Initial/empty form data */
  emptyForm: TFormData;
  /** Get unique identifier from entity */
  getEntityId: (entity: TEntity) => string;
  /** Convert entity to form data */
  entityToForm: (entity: TEntity) => TFormData;
  /** Convert form data to create DTO */
  formToCreateDto: (form: TFormData) => TCreateDto;
  /** Convert form data to update DTO */
  formToUpdateDto: (form: TFormData) => TUpdateDto;
  /** Validate form data — return a map of field name to error message (null = valid) */
  validateForm: (form: TFormData) => Record<string, string | null>;
  /** Check if form has changes compared to selected entity */
  hasFormChanges: (form: TFormData, entity: TEntity | null, isNew: boolean) => boolean;
  /** Optional callback when entities are updated */
  onEntitiesUpdated?: () => void;
  /** Optional ID of entity to select initially when modal opens */
  initialSelectedId?: string;
}

/**
 * Test result with error information
 */
export interface TestResultWithError {
  error?: string;
  /** Mirror of `error` under the field name the role-test panels render. */
  error_message?: string;
  detail?: string;
  success: boolean;
  role_type?: string;
}

/**
 * Return type for the entity management hook
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface UseEntityManagementReturn<TEntity, TFormData, TTestResult = any> {
  // State
  entities: TEntity[];
  loading: boolean;
  error: string | null;
  selectedEntity: TEntity | null;
  formData: TFormData;
  saving: boolean;
  isNew: boolean;
  showDeleteConfirm: boolean;
  successMessage: string | null;
  testing: boolean;
  testResult: TTestResult | TestResultWithError | null;
  copySuccess: boolean;

  // Actions
  loadEntities: () => Promise<void>;
  handleSelect: (entity: TEntity) => void;
  /** Swap in a fresher copy of the selected entity without resetting the form:
      `merge` gets the form as typed and the fresh entity's form, and returns
      the form to keep. */
  refreshSelected: (entity: TEntity, merge: (typed: TFormData, fresh: TFormData) => TFormData) => void;
  handleAddNew: () => void;
  handleSave: () => Promise<void>;
  handleDeleteClick: () => void;
  handleDeleteConfirm: () => Promise<void>;
  handleDeleteCancel: () => void;
  handleChange: <K extends keyof TFormData>(field: K, value: TFormData[K]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleTestRole: (...args: any[]) => Promise<void>;
  handleCopyTestResult: () => Promise<void>;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
  isFormValid: () => boolean;
  hasChanges: () => boolean;
  fieldErrors: Record<string, string | null>;
  hasAttemptedSave: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface EntityState<TEntity, TFormData, TTestResult = any> {
  selectedEntity: TEntity | null;
  selectedEntityId: string | null;
  formData: TFormData;
  isNew: boolean;
  saving: boolean;
  error: string | null;
  successMessage: string | null;
  showDeleteConfirm: boolean;
  testing: boolean;
  testResult: TTestResult | TestResultWithError | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EntityAction<TEntity, TFormData, TTestResult = any> =
  | { type: 'SELECT_ENTITY'; entity: TEntity; id: string; formData: TFormData }
  | { type: 'REFRESH_SELECTED'; entity: TEntity; mergeForm: (typed: TFormData) => TFormData }
  | { type: 'ADD_NEW'; emptyForm: TFormData }
  | { type: 'SET_FORM_FIELD'; field: string; value: unknown }
  | { type: 'SET_SAVING'; saving: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SUCCESS'; message: string | null }
  | { type: 'SET_SHOW_DELETE_CONFIRM'; show: boolean }
  | { type: 'SET_TESTING'; testing: boolean }
  | { type: 'SET_TEST_RESULT'; result: TTestResult | TestResultWithError | null }
  | { type: 'RESET'; emptyForm: TFormData }
  | { type: 'SET_IS_NEW'; isNew: boolean }
  | { type: 'SET_SELECTED_ENTITY_ID'; id: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function entityReducer<TEntity, TFormData, TTestResult = any>(
  state: EntityState<TEntity, TFormData, TTestResult>,
  action: EntityAction<TEntity, TFormData, TTestResult>,
): EntityState<TEntity, TFormData, TTestResult> {
  switch (action.type) {
    case 'SELECT_ENTITY':
      return {
        ...state,
        selectedEntity: action.entity,
        selectedEntityId: action.id,
        formData: action.formData,
        isNew: false,
        error: null,
        testResult: null,
      };
    case 'REFRESH_SELECTED':
      return { ...state, selectedEntity: action.entity, formData: action.mergeForm(state.formData) };
    case 'ADD_NEW':
      return {
        ...state,
        selectedEntity: null,
        selectedEntityId: null,
        formData: action.emptyForm,
        isNew: true,
        error: null,
        testResult: null,
      };
    case 'SET_FORM_FIELD':
      return { ...state, formData: { ...state.formData, [action.field]: action.value } };
    case 'SET_SAVING':
      return { ...state, saving: action.saving };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_SUCCESS':
      return { ...state, successMessage: action.message };
    case 'SET_SHOW_DELETE_CONFIRM':
      return { ...state, showDeleteConfirm: action.show };
    case 'SET_TESTING':
      return { ...state, testing: action.testing };
    case 'SET_TEST_RESULT':
      return { ...state, testResult: action.result };
    case 'RESET':
      return {
        ...state,
        selectedEntity: null,
        selectedEntityId: null,
        formData: action.emptyForm,
        isNew: false,
        error: null,
        testResult: null,
      };
    case 'SET_IS_NEW':
      return { ...state, isNew: action.isNew };
    case 'SET_SELECTED_ENTITY_ID':
      return { ...state, selectedEntityId: action.id };
    default:
      return state;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createInitialState<TEntity, TFormData, TTestResult = any>(
  emptyForm: TFormData,
): EntityState<TEntity, TFormData, TTestResult> {
  return {
    selectedEntity: null,
    selectedEntityId: null,
    formData: emptyForm,
    isNew: false,
    saving: false,
    error: null,
    successMessage: null,
    showDeleteConfirm: false,
    testing: false,
    testResult: null,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useEntityManagement<TEntity, TFormData, TCreateDto = any, TUpdateDto = any, TTestResult = any>(
  config: EntityManagementConfig<TEntity, TFormData, TCreateDto, TUpdateDto, TTestResult>
): UseEntityManagementReturn<TEntity, TFormData, TTestResult> {
  const {
    service,
    queryKey,
    isOpen,
    emptyForm,
    getEntityId,
    entityToForm,
    formToCreateDto,
    formToUpdateDto,
    validateForm,
    hasFormChanges,
    onEntitiesUpdated,
    initialSelectedId
  } = config;

  const [state, dispatch] = useReducer(entityReducer<TEntity, TFormData, TTestResult>, emptyForm, createInitialState);
  const { copied: copySuccess, copy: copyToClipboard } = useClipboard();
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const queryClient = useQueryClient();

  // The entity list is server state: read it through the cache, keyed by the
  // caller's scope. Loads when the modal opens; writes below invalidate it.
  const query = useQuery({
    queryKey,
    queryFn: () => service.getAll(),
    enabled: isOpen,
  });
  const entities = useMemo(() => query.data ?? [], [query.data]);

  useEffect(() => {
    if (query.error) console.error('Failed to load entities:', query.error);
  }, [query.error]);

  // A stable ref to the key so the invalidate helpers don't churn identity when a
  // caller passes a fresh array literal each render.
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const selectedIdRef = useRef(state.selectedEntityId);
  selectedIdRef.current = state.selectedEntityId;
  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeyRef.current }),
    [queryClient],
  );

  const selectEntityById = useCallback((entitiesData: TEntity[], id: string | null) => {
    if (!id) return null;
    const entity = entitiesData.find(e => getEntityId(e) === id);
    if (entity) {
      dispatch({ type: 'SELECT_ENTITY', entity, id, formData: entityToForm(entity) });
    }
    return entity;
  }, [getEntityId, entityToForm]);

  // Clear selection/form when the modal (re)opens; the selection effect below then
  // picks initialSelectedId or the first entity once the list is loaded.
  useEffect(() => {
    if (isOpen) {
      setHasAttemptedSave(false);
      dispatch({ type: 'RESET', emptyForm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSelectedId]);

  // Auto-select once the list is present: keep a still-valid selection, otherwise
  // choose initialSelectedId (if present) or the first entity. Never clobbers an
  // in-progress "add new" form.
  useEffect(() => {
    if (entities.length === 0 || state.isNew) return;
    const hasValidSelection = state.selectedEntityId != null
      && entities.some(e => getEntityId(e) === state.selectedEntityId);
    if (hasValidSelection) return;
    const preferred = initialSelectedId && entities.some(e => getEntityId(e) === initialSelectedId)
      ? initialSelectedId
      : getEntityId(entities[0]);
    selectEntityById(entities, preferred);
  }, [entities, state.isNew, state.selectedEntityId, initialSelectedId, getEntityId, selectEntityById]);

  // A public refresh (callers use it after an out-of-hook write, e.g. publish or a
  // customer update): refetch the list, then re-sync the selected entity + form from
  // the fresh data so the detail panel reflects the server (maintainSelection).
  const loadEntities = useCallback(async () => {
    await invalidate();
    const fresh = queryClient.getQueryData<TEntity[]>(queryKeyRef.current);
    const id = selectedIdRef.current;
    if (fresh && id) {
      const entity = fresh.find((e) => getEntityId(e) === id);
      if (entity) dispatch({ type: 'SELECT_ENTITY', entity, id: getEntityId(entity), formData: entityToForm(entity) });
    }
  }, [invalidate, queryClient, getEntityId, entityToForm]);

  const handleSelect = (entity: TEntity) => {
    setHasAttemptedSave(false);
    dispatch({ type: 'SELECT_ENTITY', entity, id: getEntityId(entity), formData: entityToForm(entity) });
  };

  const refreshSelected = (
    entity: TEntity,
    merge: (typed: TFormData, fresh: TFormData) => TFormData,
  ) => {
    const fresh = entityToForm(entity);
    dispatch({ type: 'REFRESH_SELECTED', entity, mergeForm: (typed) => merge(typed, fresh) });
  };

  const handleAddNew = () => {
    setHasAttemptedSave(false);
    dispatch({ type: 'ADD_NEW', emptyForm });
  };

  const handleSave = async () => {
    setHasAttemptedSave(true);
    const errors = validateForm(state.formData);
    if (Object.values(errors).some(e => e !== null)) return;

    try {
      dispatch({ type: 'SET_SAVING', saving: true });
      dispatch({ type: 'SET_ERROR', error: null });
      dispatch({ type: 'SET_SUCCESS', message: null });

      if (state.isNew) {
        const created = await service.create(formToCreateDto(state.formData));
        await invalidate();
        dispatch({ type: 'SELECT_ENTITY', entity: created, id: getEntityId(created), formData: entityToForm(created) });
        dispatch({ type: 'SET_SUCCESS', message: 'Item created successfully' });
      } else if (state.selectedEntity) {
        const id = getEntityId(state.selectedEntity);
        const updated = await service.update(id, formToUpdateDto(state.formData));
        await invalidate();
        dispatch({ type: 'SELECT_ENTITY', entity: updated, id: getEntityId(updated), formData: entityToForm(updated) });
        dispatch({ type: 'SET_SUCCESS', message: 'Item updated successfully' });
      }

      dispatch({ type: 'SET_IS_NEW', isNew: false });
      setHasAttemptedSave(false);
      setTimeout(() => dispatch({ type: 'SET_SUCCESS', message: null }), 3000);

      if (onEntitiesUpdated) {
        onEntitiesUpdated();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      dispatch({ type: 'SET_ERROR', error: `Failed to ${state.isNew ? 'create' : 'update'} item: ${message}` });
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  };

  const handleDeleteClick = () => {
    dispatch({ type: 'SET_SHOW_DELETE_CONFIRM', show: true });
  };

  const handleDeleteConfirm = async () => {
    if (!state.selectedEntity) return;

    try {
      dispatch({ type: 'SET_SAVING', saving: true });
      dispatch({ type: 'SET_ERROR', error: null });
      dispatch({ type: 'SET_SUCCESS', message: null });
      dispatch({ type: 'SET_SHOW_DELETE_CONFIRM', show: false });

      const id = getEntityId(state.selectedEntity);
      await service.delete(id);
      await invalidate();

      // Clear selection; the selection effect re-selects the first remaining
      // entity once the refetched list arrives (or leaves none if empty).
      dispatch({ type: 'RESET', emptyForm });
      dispatch({ type: 'SET_SUCCESS', message: 'Item deleted successfully' });
      setTimeout(() => dispatch({ type: 'SET_SUCCESS', message: null }), 3000);

      if (onEntitiesUpdated) {
        onEntitiesUpdated();
      }
    } catch (err) {
      let errorMessage = 'Failed to delete item';
      if (err && typeof err === 'object') {
        const axiosError = err as import('../../types/frontend').ApiErrorResponse;
        if (axiosError.response?.status === 403) {
          errorMessage = 'Permission denied. You may not have access to delete this item.';
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }
      dispatch({ type: 'SET_ERROR', error: errorMessage });
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  };

  const handleDeleteCancel = () => {
    dispatch({ type: 'SET_SHOW_DELETE_CONFIRM', show: false });
  };

  const handleChange = <K extends keyof TFormData>(field: K, value: TFormData[K]) => {
    dispatch({ type: 'SET_FORM_FIELD', field: field as string, value });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTestRole = async (...args: any[]) => {
    if (!state.selectedEntity || !service.testAccess) return;

    try {
      dispatch({ type: 'SET_TESTING', testing: true });
      dispatch({ type: 'SET_ERROR', error: null });
      dispatch({ type: 'SET_TEST_RESULT', result: null });
      const id = getEntityId(state.selectedEntity);
      const result = await service.testAccess(id, ...args);
      dispatch({ type: 'SET_TEST_RESULT', result });
    } catch (err) {
      const { message: errorMessage, detail: errorDetail } = extractErrorWithDetail(err, 'Failed to test access');
      dispatch({
        type: 'SET_TEST_RESULT',
        result: {
          error: errorMessage,
          error_message: errorMessage,
          detail: errorDetail,
          success: false,
          role_type: typeof args[0] === 'string' ? args[0] : undefined,
        },
      });
    } finally {
      dispatch({ type: 'SET_TESTING', testing: false });
    }
  };

  const handleCopyTestResult = async () => {
    if (!state.testResult) return;
    await copyToClipboard(JSON.stringify(state.testResult, null, 2));
  };

  const rawErrors = validateForm(state.formData);
  const isFormValid = () => Object.values(rawErrors).every(e => e === null);
  const fieldErrors = useMemo(() => {
    if (!hasAttemptedSave) {
      return Object.fromEntries(Object.keys(rawErrors).map(k => [k, null]));
    }
    return rawErrors;
  }, [hasAttemptedSave, rawErrors]);
  const hasChanges = () => hasFormChanges(state.formData, state.selectedEntity, state.isNew);

  const setError = (error: string | null) => dispatch({ type: 'SET_ERROR', error });
  const setSuccessMessage = (message: string | null) => dispatch({ type: 'SET_SUCCESS', message });

  return {
    entities,
    // isLoading (first load only): a silent post-write refetch (invalidate) must not
    // toggle the list into a loading state.
    loading: query.isLoading,
    // Save/delete/test errors come from the reducer; a list-load failure is derived
    // from the query.
    error: state.error ?? (query.isError ? 'Failed to load items. Please try again.' : null),
    selectedEntity: state.selectedEntity,
    formData: state.formData,
    saving: state.saving,
    isNew: state.isNew,
    showDeleteConfirm: state.showDeleteConfirm,
    successMessage: state.successMessage,
    testing: state.testing,
    testResult: state.testResult,
    copySuccess,

    loadEntities,
    handleSelect,
    refreshSelected,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    handleTestRole,
    handleCopyTestResult,
    setError,
    setSuccessMessage,
    isFormValid,
    hasChanges,
    fieldErrors,
    hasAttemptedSave
  };
}
